import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { NAV_LINKS, SITE } from "@/lib/site";

export default function Navbar() {
    const [open, setOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    return (
        <header
            data-testid="site-header"
            style={{
                position: "sticky",
                top: 0,
                zIndex: 50,
                backgroundColor: "var(--brand-cream)",
                borderBottom: "1px solid var(--brand-surface)",
                backdropFilter: "saturate(180%) blur(6px)",
            }}
        >
            <div
                className="brand-container"
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    paddingTop: "0.85rem",
                    paddingBottom: "0.85rem",
                    gap: "1rem",
                }}
            >
                <Link
                    to="/"
                    aria-label="CPR Paint & Coatings home"
                    data-testid="nav-logo-link"
                    style={{ display: "inline-flex", alignItems: "center" }}
                >
                    <img
                        src="/brand/logo-cropped-1024.png"
                        alt="CPR Paint & Coatings"
                        className="nav-logo-img"
                        style={{ display: "block", height: "auto" }}
                    />
                </Link>

                {/* Desktop nav */}
                <nav
                    aria-label="Primary"
                    className="hidden md:flex"
                    style={{ alignItems: "center", gap: "2rem" }}
                >
                    {NAV_LINKS.slice(0, 3).map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            end={l.to === "/"}
                            data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                            style={({ isActive }) => ({
                                fontFamily: "var(--font-display)",
                                fontWeight: 600,
                                fontSize: "0.95rem",
                                color: isActive
                                    ? "var(--brand-red)"
                                    : "var(--brand-dark)",
                                borderBottom: isActive
                                    ? "2px solid var(--brand-red)"
                                    : "2px solid transparent",
                                paddingBottom: 2,
                                transition: "color 150ms ease",
                            })}
                        >
                            {l.label}
                        </NavLink>
                    ))}
                </nav>

                <div
                    className="hidden md:flex"
                    style={{ alignItems: "center", gap: "1rem" }}
                >
                    <a
                        href={`tel:${SITE.phoneLink}`}
                        data-testid="nav-phone-link"
                        style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 6,
                            fontWeight: 600,
                            fontSize: "0.9rem",
                            color: "var(--brand-dark)",
                        }}
                    >
                        <Phone size={16} color="var(--brand-red)" />
                        {SITE.phoneDisplay}
                    </a>
                    <Link
                        to="/contact"
                        data-testid="nav-cta-quote"
                        className="btn btn-primary"
                        style={{ padding: "0.65rem 1.15rem" }}
                    >
                        Get a Free Estimate
                    </Link>
                </div>

                {/* Mobile toggle */}
                <button
                    type="button"
                    aria-label="Toggle menu"
                    aria-expanded={open}
                    data-testid="nav-mobile-toggle"
                    onClick={() => setOpen((v) => !v)}
                    className="md:hidden"
                    style={{
                        background: "transparent",
                        border: "none",
                        padding: 6,
                        cursor: "pointer",
                        color: "var(--brand-dark)",
                    }}
                >
                    {open ? <X size={26} /> : <Menu size={26} />}
                </button>
            </div>

            {/* Mobile menu */}
            {open && (
                <div
                    data-testid="nav-mobile-menu"
                    className="md:hidden"
                    style={{
                        borderTop: "1px solid var(--brand-surface)",
                        backgroundColor: "var(--brand-cream)",
                    }}
                >
                    <nav
                        className="brand-container"
                        aria-label="Mobile"
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "0.25rem",
                            paddingTop: "0.75rem",
                            paddingBottom: "1.25rem",
                        }}
                    >
                        {NAV_LINKS.map((l) => (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                end={l.to === "/"}
                                data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                                style={({ isActive }) => ({
                                    fontFamily: "var(--font-display)",
                                    fontWeight: 600,
                                    fontSize: "1.05rem",
                                    padding: "0.85rem 0.25rem",
                                    color: isActive
                                        ? "var(--brand-red)"
                                        : "var(--brand-dark)",
                                    borderBottom:
                                        "1px solid var(--brand-surface)",
                                })}
                            >
                                {l.label}
                            </NavLink>
                        ))}
                        <a
                            href={`tel:${SITE.phoneLink}`}
                            data-testid="nav-mobile-phone-link"
                            style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 8,
                                padding: "0.85rem 0.25rem",
                                fontWeight: 600,
                                color: "var(--brand-dark)",
                            }}
                        >
                            <Phone size={16} color="var(--brand-red)" />
                            {SITE.phoneDisplay}
                        </a>
                    </nav>
                </div>
            )}
        </header>
    );
}
